import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Plus, Edit2, Trash2, X, AlertCircle, Truck, Upload, CheckCircle, AlertTriangle, FileDown, List, Download, ShieldAlert, Lock, Unlock, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';

import API, { getVehicles, getVehicle, createVehicle, updateVehicle, deleteVehicle, deleteAllVehicles, importVehicles, downloadVehicleTemplate, getVehicleFilterOptions, renameMasterData, deleteMasterData, getKaryawan, getKlien, upsertKlien, getKlienHoldSetting, updateKlienHoldSetting } from '../api';
import { useAuth } from '../AuthContext';

const emptyForm = {
  plate_no: '',
  body_no: '',
  operator: '',
  ketua_koperasi: '',
  route_code: '',
  vehicle_type: '',
  jenis_ac: 'Non AC',
  is_active: true,
  pengendapan: '',
  contract_no: '',
  teknisi: '',
};

export default function Vehicles() {
  const { user, hasMenuAction } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [filters, setFilters] = useState({
    filter_operator: '',
    filter_route: '',
    filter_vehicle_type: '',
    filter_jenis_ac: '',
    filter_is_active: 'true',
    filter_contract_no: '',
    filter_teknisi: '',
    filter_pengendapan: '',
  });
  const [filterOptions, setFilterOptions] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingAll, setDeletingAll] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const fileInputRef = useRef(null);
  const [masterData, setMasterData] = useState({ field: '', title: '', options: [] });
  const [showMasterModal, setShowMasterModal] = useState(false);
  const [teknisiList, setTeknisiList] = useState([]);
  const [loadingTeknisi, setLoadingTeknisi] = useState(false);
  const [masterNewValue, setMasterNewValue] = useState('');
  const [masterAddHint, setMasterAddHint] = useState('');
  const [editingMasterItem, setEditingMasterItem] = useState(null);
  const [masterEditValue, setMasterEditValue] = useState('');
  const [masterSaving, setMasterSaving] = useState(false);
  const [klienList, setKlienList] = useState([]); // master klien: [{id, nama, ketua_koperasi}]
  const [masterKetuaValue, setMasterKetuaValue] = useState(''); // ketua koperasi klien baru
  const [editKetuaFor, setEditKetuaFor] = useState(null); // nama klien yang sedang diedit ketuanya
  const [editKetuaValue, setEditKetuaValue] = useState('');
  // Kontrol manual blokir input CM/PM untuk klien BDL (auto / paksa blokir / bebas)
  const [holdSetting, setHoldSetting] = useState(null);
  const [holdCatatan, setHoldCatatan] = useState('');
  const [holdSaving, setHoldSaving] = useState(false);
  const limit = 15;

  const loadData = useCallback(() => {
    setLoading(true);
    getVehicles({ page, limit, search, status: statusFilter, ...filters })
      .then((vehRes) => {
        setData(vehRes.data);
      })
      .catch(err => console.error('Failed to load vehicles', err))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load filter options (distinct values for dropdowns)
  useEffect(() => {
    getVehicleFilterOptions()
      .then(res => setFilterOptions(res.data))
      .catch(err => console.error('Failed to load filter options', err));
  }, []);

  // Status kontrol blokir input CM/PM klien BDL (hanya admin yang bisa mengubah)
  const loadHoldSetting = useCallback(() => {
    getKlienHoldSetting()
      .then(res => {
        setHoldSetting(res.data);
        setHoldCatatan(res.data?.setting?.catatan || '');
      })
      .catch(err => console.error('Failed to load klien hold setting', err));
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') loadHoldSetting();
  }, [user, loadHoldSetting]);

  const ubahHoldMode = async (mode) => {
    if (holdSaving) return;
    setHoldSaving(true);
    try {
      await updateKlienHoldSetting({ mode, catatan: holdCatatan });
      loadHoldSetting();
    } catch (err) {
      alert(err?.response?.data?.error || 'Gagal menyimpan kontrol blokir CM/PM');
    } finally {
      setHoldSaving(false);
    }
  };
  const exportExcel = async () => {
    try {
      // Fetch semua data kendaraan (termasuk filter aktif)
      const params = { all: 'true' };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await API.get('/vehicles', { params });
      const vehicles = res.data?.data || [];
      if (vehicles.length === 0) {
        alert('Tidak ada data kendaraan untuk di-export');
        return;
      }

      // Siapkan data untuk worksheet
      const rows = vehicles.map((v, i) => ({
        'No': i + 1,
        'Nama Klien': v.operator || '',
        'Ketua Koperasi': v.ketua_koperasi || '',
        'No. Body': v.body_no || '',
        'No. Polisi': v.plate_no || '',
        'Rute': v.route_code || '',
        'Jenis Armada': v.vehicle_type || '',
        'AC/Non AC': v.jenis_ac === 'AC' ? 'AC' : 'Non AC',
        'Status': v.is_active ? 'Aktif' : 'Non-Aktif',
        'Pengendapan': v.pengendapan || '',
        'No. Kontrak': v.contract_no || '',
        'Teknisi': v.teknisi || '',
      }));

      // Buat workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);

      // Atur lebar kolom
      ws['!cols'] = [
        { wch: 5 },   // No
        { wch: 25 },  // Nama Klien
        { wch: 22 },  // Ketua Koperasi
        { wch: 14 },  // No. Body
        { wch: 14 },  // No. Polisi
        { wch: 10 },  // Rute
        { wch: 16 },  // Jenis Armada
        { wch: 10 },  // AC/Non AC
        { wch: 10 },  // Status
        { wch: 18 },  // Pengendapan
        { wch: 16 },  // No. Kontrak
        { wch: 20 },  // Teknisi
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Data Kendaraan');

      // Download
      const now = new Date();
      const filename = `Data_Kendaraan_${now.toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error('Export Excel gagal', err);
      alert('Gagal mengexport Excel. Silahkan coba lagi.');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const loadTeknisiList = async () => {
    if (teknisiList.length > 0) return;
    setLoadingTeknisi(true);
    try {
      const res = await getKaryawan();
      const allKaryawan = res.data || [];
      // Filter karyawan with bagian 'Teknisi' or jabatan containing 'teknisi'
      const teknisi = allKaryawan.filter(k => 
        (k.bagian_name && k.bagian_name.toLowerCase().includes('teknisi')) ||
        (k.jabatan && k.jabatan.toLowerCase().includes('teknisi'))
      );
      setTeknisiList(teknisi);
    } catch (err) {
      console.error('Gagal load teknisi', err);
    } finally {
      setLoadingTeknisi(false);
    }
  };

  const openCreate = () => {
    setEditingVehicle(null);
    setForm(emptyForm);
    setError('');
    loadTeknisiList();
    setShowModal(true);
  };

  const openEdit = async (vehicle) => {
    setEditingVehicle(vehicle);
    setError('');
    try {
      // Load full vehicle details
      const res = await getVehicle(vehicle.id);
      const v = res.data;
      setForm({
        plate_no: v.plate_no || '',
        body_no: v.body_no || '',
        operator: v.operator || '',
        ketua_koperasi: v.ketua_koperasi || '',
        route_code: v.route_code || '',
        vehicle_type: v.vehicle_type || '',
        jenis_ac: v.jenis_ac || 'Non AC',
        is_active: v.is_active !== undefined ? v.is_active : true,
        pengendapan: v.pengendapan || '',
        contract_no: v.contract_no || '',
        teknisi: v.teknisi || '',
      });
      loadTeknisiList();
      setShowModal(true);
    } catch (err) {
      setError('Gagal memuat data kendaraan');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadVehicleTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_import_kendaraan.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download template gagal', err);
      alert('Gagal mendownload template. Silahkan coba lagi.');
    }
  };

  const handleSave = async () => {
    setError('');
    if (!form.plate_no) { setError('Nomor polisi harus diisi'); return; }

    setSaving(true);
    try {
      const payload = { ...form };

      if (editingVehicle) {
        await updateVehicle(editingVehicle.id, payload);
      } else {
        await createVehicle(payload);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan kendaraan');
    } finally {
      setSaving(false);
    }
  };

  const openMasterData = (field, title) => {
    // Reset state operasi master — cegah tombol Tambah macet disabled
    setMasterSaving(false);
    setMasterAddHint('');
    let options = [];
    if (field === 'operator') {
      options = filterOptions?.operators || [];
      // Muat master klien (nama + ketua koperasi)
      getKlien().then(res => setKlienList(res.data || [])).catch(() => setKlienList([]));
    }
    if (field === 'route_code') options = filterOptions?.routes || [];
    if (field === 'vehicle_type') options = filterOptions?.vehicle_types || [];
    if (field === 'pengendapan') options = filterOptions?.pengendapan_list || [];
    // Sort and deduplicate
    options = [...new Set(options)].filter(Boolean).sort();
    setMasterData({ field, title, options });
    setMasterNewValue('');
    setMasterKetuaValue('');
    setEditKetuaFor(null);
    setShowMasterModal(true);
  };

  const closeMasterModal = () => {
    setMasterSaving(false);
    setMasterAddHint('');
    setShowMasterModal(false);
  };

  const selectMasterData = (value) => {
    if (masterData.field === 'operator') {
      // Pilih klien → isi nama klien + ketua koperasi sekaligus
      const kl = klienList.find(k => k.nama === value);
      setForm(prev => ({ ...prev, operator: value, ketua_koperasi: kl ? (kl.ketua_koperasi || '') : '' }));
    } else {
      setForm(prev => ({ ...prev, [masterData.field]: value }));
    }
    closeMasterModal();
  };

  const getOptionsFromData = (data, field) => {
    let all = [];
    if (field === 'operator') all = data?.operators || [];
    if (field === 'route_code') all = data?.routes || [];
    if (field === 'vehicle_type') all = data?.vehicle_types || [];
    if (field === 'pengendapan') all = data?.pengendapan_list || [];
    return [...new Set(all)].filter(Boolean).sort();
  };

  const addMasterData = async () => {
    const val = masterNewValue.trim();
    if (!val) {
      setMasterAddHint('Isi nama klien terlebih dahulu, lalu klik Tambah.');
      return;
    }
    setMasterAddHint('');
    setMasterSaving(true);
    // Simpan ke master klien (operator + ketua koperasi)
    if (masterData.field === 'operator') {
      try {
        await upsertKlien({ nama: val, ketua_koperasi: masterKetuaValue });
        const res = await getKlien();
        setKlienList(res.data || []);
      } catch (err) {
        alert('Gagal simpan klien: ' + (err.response?.data?.error || err.message));
        setMasterSaving(false);
        return;
      }
    }
    // Add to filterOptions locally so it persists in the session
    const updatedOptions = [...new Set([...masterData.options, val])].sort();
    const updatedFilter = { ...filterOptions };
    if (masterData.field === 'operator') updatedFilter.operators = updatedOptions;
    if (masterData.field === 'route_code') updatedFilter.routes = updatedOptions;
    if (masterData.field === 'vehicle_type') updatedFilter.vehicle_types = updatedOptions;
    if (masterData.field === 'pengendapan') updatedFilter.pengendapan_list = updatedOptions;
    setFilterOptions(updatedFilter);
    setMasterData(prev => ({ ...prev, options: updatedOptions }));
    setMasterNewValue('');
    setMasterKetuaValue('');
    setMasterSaving(false);
  };

  // Simpan/edit ketua koperasi untuk satu klien
  const saveKetua = async (nama) => {
    if (!nama) return;
    setMasterSaving(true);
    try {
      await upsertKlien({ nama, ketua_koperasi: editKetuaValue });
      const res = await getKlien();
      setKlienList(res.data || []);
      setEditKetuaFor(null);
      setEditKetuaValue('');
    } catch (err) {
      alert('Gagal simpan ketua koperasi: ' + (err.response?.data?.error || err.message));
    } finally {
      setMasterSaving(false);
    }
  };

  const startEditMasterItem = (item) => {
    setEditingMasterItem(item);
    setMasterEditValue(item);
  };

  const saveEditMasterItem = async () => {
    const newVal = masterEditValue.trim();
    if (!newVal || !editingMasterItem) return;
    if (newVal === editingMasterItem) {
      setEditingMasterItem(null);
      return;
    }
    setMasterSaving(true);
    try {
      await renameMasterData(masterData.field, editingMasterItem, newVal);
      // Refresh filter options
      const res = await getVehicleFilterOptions();
      setFilterOptions(res.data);
      setMasterData(prev => ({ ...prev, options: getOptionsFromData(res.data, prev.field) }));
      setEditingMasterItem(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal mengubah data');
    } finally {
      setMasterSaving(false);
    }
  };

  const deleteMasterDataItem = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus "${item}"? Data ini akan dikosongkan dari semua kendaraan yang menggunakannya.`)) return;
    setMasterSaving(true);
    try {
      await deleteMasterData(masterData.field, item);
      // Refresh filter options
      const res = await getVehicleFilterOptions();
      setFilterOptions(res.data);
      setMasterData(prev => ({ ...prev, options: getOptionsFromData(res.data, prev.field) }));
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus data');
    } finally {
      setMasterSaving(false);
    }
  };

  const handleDelete = async (vehicle) => {
    if (!window.confirm(`Yakin ingin menghapus kendaraan "${vehicle.plate_no}"?`)) return;
    try {
      await deleteVehicle(vehicle.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus kendaraan');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('⚠️ PERINGATAN!\n\nAnda akan menghapus SEMUA data kendaraan secara permanen.\nTindakan ini TIDAK dapat dibatalkan!\n\nApakah Anda yakin ingin melanjutkan?')) return;

    const secondConfirm = window.confirm('KONFIRMASI AKHIR\n\nApakah Anda benar-benar yakin ingin menghapus seluruh database kendaraan?\n\nSemua data akan hilang permanen!');
    if (!secondConfirm) return;

    setDeletingAll(true);
    try {
      await deleteAllVehicles();
      alert('✅ Semua data kendaraan berhasil dihapus.');
      setPage(1);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus data kendaraan');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError('File harus berformat Excel (.xlsx atau .xls)');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setImportResult(null);
    setShowImportModal(true);

    try {
      const res = await importVehicles(file, (progressEvent) => {
        if (progressEvent.total) {
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      });
      const data = res.data;
      setImportResult(data);
      if (data.imported > 0) {
        loadData();
      }
    } catch (err) {
      setImportResult({
        error: err.response?.data?.error || err.message || 'Gagal mengupload file',
      });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="page vehicles-page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Database Kendaraan</h2>
            <p className="page-desc">Total {data?.total?.toLocaleString() || 0} kendaraan terdaftar</p>
          </div>
          <div className="page-header-actions">
            {hasMenuAction('vehicles', 'create') && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <button className="btn btn-secondary" onClick={handleDownloadTemplate}>
                  <FileDown size={16} />
                  <span>Download Template</span>
                </button>
                <button className="btn btn-secondary" onClick={handleImportClick}>
                  <Upload size={16} />
                  <span>Import Excel</span>
                </button>
                <button className="btn btn-secondary" onClick={exportExcel}>
                  <Download size={16} />
                  <span>Export Excel</span>
                </button>
                <button className="btn btn-primary" onClick={openCreate}>
                  <Plus size={16} />
                  <span>Tambah Kendaraan</span>
                </button>
                {user?.role === 'admin' && (
                  <button
                    className="btn btn-danger-outline"
                    onClick={handleDeleteAll}
                    disabled={deletingAll}
                    title="Hapus semua data kendaraan (hanya admin)"
                    style={{
                      marginLeft: 8,
                      border: '1px solid var(--danger, #e74c3c)',
                      color: 'var(--danger, #e74c3c)',
                      background: 'transparent',
                    }}
                  >
                    {deletingAll ? (
                      <span>Menghapus...</span>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        <span>Hapus Semua Data</span>
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {user?.role === 'admin' && holdSetting && (() => {
        const mode = holdSetting.setting?.mode || 'auto';
        const otomatis = holdSetting.otomatis || {};
        const efektif = holdSetting.efektif || {};
        const badge = mode === 'blokir'
          ? { text: 'Diblokir manual', bg: '#fee2e2', color: '#b91c1c' }
          : mode === 'bebas'
            ? { text: 'Dibebaskan manual', bg: '#e0f2fe', color: '#0369a1' }
            : efektif.blokir
              ? { text: 'Otomatis — sedang diblokir', bg: '#fef3c7', color: '#b45309' }
              : { text: 'Otomatis — tidak diblokir', bg: '#dcfce7', color: '#15803d' };
        const btn = (key, label, Icon, color) => {
          const aktif = mode === key;
          return (
            <button key={key} type="button" onClick={() => ubahHoldMode(key)} disabled={holdSaving}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
                borderRadius: 8, fontSize: '0.8rem', fontWeight: 600, cursor: holdSaving ? 'wait' : 'pointer',
                border: `1px solid ${aktif ? color : '#e2e8f0'}`,
                background: aktif ? color : '#fff', color: aktif ? '#fff' : '#475569',
                opacity: holdSaving ? 0.7 : 1,
              }}>
              <Icon size={14} /> {label}
            </button>
          );
        };
        return (
          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
            padding: '12px 14px', marginBottom: 14,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 300 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.9rem', flexWrap: 'wrap' }}>
                  <ShieldAlert size={16} color="#e11d48" />
                  Blokir Input CM/PM — Klien {holdSetting.setting?.label || 'BDL'}
                  <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, background: badge.bg, color: badge.color }}>
                    {badge.text}
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 5, lineHeight: 1.5 }}>
                  Otomatis: tagihan tgl 1–15 jatuh tempo tgl 20; tagihan tgl 16–akhir bulan jatuh tempo tgl 5 bulan berikutnya.
                  {' '}Saat ini <strong>{otomatis.jumlah_tagihan || 0}</strong> tagihan belum lunas lewat jatuh tempo
                  {otomatis.total_nominal ? ` (total Rp ${Number(otomatis.total_nominal).toLocaleString('id-ID')})` : ''}
                  {otomatis.jatuh_tempo_terakhir ? `, jatuh tempo terakhir ${otomatis.jatuh_tempo_terakhir}` : ''}.
                </div>
                {holdSetting.setting?.updated_by && (
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 3 }}>
                    Terakhir diubah: {holdSetting.setting.updated_by} · {holdSetting.setting.updated_at || '-'}
                    {holdSetting.setting.catatan ? ` · Catatan: ${holdSetting.setting.catatan}` : ''}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {btn('auto', 'Otomatis', RefreshCw, '#16a34a')}
                  {btn('blokir', 'Paksa Blokir', Lock, '#dc2626')}
                  {btn('bebas', 'Bebaskan', Unlock, '#0284c7')}
                </div>
                <input type="text" value={holdCatatan} onChange={e => setHoldCatatan(e.target.value)}
                  placeholder="Catatan perubahan (opsional)"
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.78rem', width: 260 }} />
              </div>
            </div>
          </div>
        );
      })()}

      <div className="filters filters-vehicles">
        <form onSubmit={handleSearch} className="search-box" style={{ minWidth: 260 }}>
          <Search size={18} />
          <input
            type="text"
            placeholder="Cari No Polisi, No Body, atau Pemilik..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </form>

        <div className="filter-dropdowns">
          <select value={filters.filter_operator} onChange={(e) => { setFilters(f => ({ ...f, filter_operator: e.target.value })); setPage(1); }}>
            <option value="">Semua Klien</option>
            {filterOptions?.operators?.map((v, i) => (
              <option key={i} value={v}>{v}</option>
            ))}
          </select>

          <select value={filters.filter_route} onChange={(e) => { setFilters(f => ({ ...f, filter_route: e.target.value })); setPage(1); }}>
            <option value="">Semua Rute</option>
            {filterOptions?.routes?.map((v, i) => (
              <option key={i} value={v}>{v}</option>
            ))}
          </select>

          <select value={filters.filter_vehicle_type} onChange={(e) => { setFilters(f => ({ ...f, filter_vehicle_type: e.target.value })); setPage(1); }}>
            <option value="">Semua Jenis Armada</option>
            {filterOptions?.vehicle_types?.map((v, i) => (
              <option key={i} value={v}>{v}</option>
            ))}
          </select>

          <select value={filters.filter_jenis_ac} onChange={(e) => { setFilters(f => ({ ...f, filter_jenis_ac: e.target.value })); setPage(1); }}>
            <option value="">AC/Non AC</option>
            {filterOptions?.jenis_ac_list?.map((v, i) => (
              <option key={i} value={v}>{v === 'AC' ? '❄ AC' : '🔧 Non AC'}</option>
            ))}
          </select>

          <select value={filters.filter_is_active} onChange={(e) => { setFilters(f => ({ ...f, filter_is_active: e.target.value })); setPage(1); }}>
            <option value="">Semua Status</option>
            <option value="true">Aktif</option>
            <option value="false">Non-Aktif</option>
          </select>

          <select value={filters.filter_teknisi} onChange={(e) => { setFilters(f => ({ ...f, filter_teknisi: e.target.value })); setPage(1); }}>
            <option value="">Semua Teknisi</option>
            {filterOptions?.teknisi_list?.map((v, i) => (
              <option key={i} value={v}>{v}</option>
            ))}
          </select>

          <select value={filters.filter_pengendapan} onChange={(e) => { setFilters(f => ({ ...f, filter_pengendapan: e.target.value })); setPage(1); }}>
            <option value="">Semua Pengendapan</option>
            {filterOptions?.pengendapan_list?.map((v, i) => (
              <option key={i} value={v}>{v}</option>
            ))}
          </select>

          <select value={filters.filter_contract_no} onChange={(e) => { setFilters(f => ({ ...f, filter_contract_no: e.target.value })); setPage(1); }}>
            <option value="">Semua No Kontrak</option>
            {filterOptions?.contract_nos?.map((v, i) => (
              <option key={i} value={v}>{v}</option>
            ))}
          </select>
        </div>

        <div className="filter-actions">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">Dokumen</option>
            <option value="stnk">STNK Expired</option>
            <option value="kir">KIR Expired</option>
            <option value="kp">KP Expired</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Memuat data...</div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 40, textAlign: 'center' }}>No</th>
                  <th>Nama Klien</th>
                  <th>Ketua Koperasi</th>
                  <th>No. Body</th>
                  <th>No. Polisi</th>
                  <th>Rute</th>
                  <th>Jenis Armada</th>
                  <th>AC/Non AC</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th>Pengendapan</th>
                  <th>No. Kontrak</th>
                  <th>Teknisi</th>
                  {(hasMenuAction('vehicles', 'edit') || hasMenuAction('vehicles', 'delete')) && (
                    <th style={{ width: 80, textAlign: 'center' }}>Aksi</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data?.data?.map((v, idx) => (
                  <tr key={v.id}>
                    <td style={{ textAlign: 'center', color: 'var(--gray-400)', fontSize: '0.78rem' }}>
                      {(page - 1) * limit + idx + 1}
                    </td>
                    <td style={{ fontWeight: 500 }}>{v.operator || '-'}</td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--gray-600)' }}>{v.ketua_koperasi || '-'}</td>
                    <td>{v.body_no || '-'}</td>
                    <td><strong style={{ color: 'var(--gray-700)' }}>{v.plate_no}</strong></td>
                    <td>{v.route_code || '-'}</td>
                    <td>{v.vehicle_type || '-'}</td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 10px', borderRadius: 20,
                        fontSize: '0.72rem', fontWeight: 600,
                        background: v.jenis_ac === 'AC' ? '#e8f4fd' : '#f0f0f0',
                        color: v.jenis_ac === 'AC' ? '#0a66c2' : '#666',
                      }}>
                        {v.jenis_ac === 'AC' ? '❄ AC' : '🔧 Non AC'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`badge ${v.is_active ? 'badge-success' : 'badge-danger'}`}
                        style={{ fontSize: '0.7rem' }}>
                        {v.is_active ? 'Aktif' : 'Non-Aktif'}
                      </span>
                    </td>
                    <td>{v.pengendapan || '-'}</td>
                    <td>{v.contract_no || '-'}</td>
                    <td>{v.teknisi || '-'}</td>
                    {(hasMenuAction('vehicles', 'edit') || hasMenuAction('vehicles', 'delete')) && (
                      <td style={{ textAlign: 'center' }}>
                        <div className="action-btns" style={{ justifyContent: 'center' }}>
                          {hasMenuAction('vehicles', 'edit') && (
                            <button className="btn-icon" title="Edit" onClick={() => openEdit(v)}>
                              <Edit2 size={14} />
                            </button>
                          )}
                          {hasMenuAction('vehicles', 'delete') && (
                            <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(v)}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {(!data?.data || data.data.length === 0) && (
                  <tr><td colSpan={12} className="text-center text-muted">Tidak ada data kendaraan</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <span className="page-info">
              Halaman {data?.page} dari {data?.totalPages} (total {data?.total} data)
            </span>
            <div className="page-buttons">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft size={16} /> Sebelumnya
              </button>
              <button disabled={page >= (data?.totalPages || 1)} onClick={() => setPage(p => p + 1)}>
                Selanjutnya <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => { if (!uploading) setShowImportModal(false); }}>
          <div className="modal-content" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Upload size={18} style={{ marginRight: 8 }} />
                Import Data Kendaraan
              </h3>
              {!uploading && (
                <button className="btn-icon" onClick={() => setShowImportModal(false)}>
                  <X size={18} />
                </button>
              )}
            </div>
            <div className="modal-body">
              {uploading ? (
                <div className="import-uploading">
                  <div className="import-progress-circle">
                    <Upload size={32} />
                  </div>
                  <p className="import-status-text">Mengupload & memproses file...</p>
                  <div className="import-progress-bar-track">
                    <div className="import-progress-bar-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="import-progress-pct">{uploadProgress}%</p>
                </div>
              ) : importResult ? (
                <div className="import-result">
                  {importResult.error ? (
                    <>
                      <div className="import-result-icon import-error-icon">
                        <AlertTriangle size={36} />
                      </div>
                      <h4>Import Gagal</h4>
                      <p className="import-error-msg">{importResult.error}</p>
                    </>
                  ) : (
                    <>
                      <div className="import-result-icon import-success-icon">
                        <CheckCircle size={36} />
                      </div>
                      <h4>Import Selesai!</h4>
                      <div className="import-stats">
                        <div className="import-stat-item">
                          <span className="import-stat-label">Diproses</span>
                          <span className="import-stat-value">{importResult.total || 0}</span>
                        </div>
                        <div className="import-stat-item import-stat-success">
                          <span className="import-stat-label">Berhasil</span>
                          <span className="import-stat-value">{importResult.imported || 0}</span>
                        </div>
                        <div className="import-stat-item import-stat-skipped">
                          <span className="import-stat-label">Diabaikan</span>
                          <span className="import-stat-value">{importResult.skipped || 0}</span>
                        </div>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="import-errors">
                          <p className="import-errors-title">
                            <AlertTriangle size={14} />
                            {importResult.errors.length} error
                          </p>
                          <ul>
                            {importResult.errors.slice(0, 5).map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                            {importResult.errors.length > 5 && (
                              <li className="text-muted">...dan {importResult.errors.length - 5} lainnya</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
            {!uploading && importResult && (
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => { setShowImportModal(false); if (importResult?.imported > 0) loadData(); }}>
                  Tutup
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Truck size={18} style={{ marginRight: 8 }} />
                {editingVehicle ? 'Edit Kendaraan' : 'Tambah Kendaraan Baru'}
              </h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="form-error">
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-grid">
                {/* Nama Klien */}
                <div className="form-group">
                  <label>Nama Klien</label>
                  <div className="input-with-btn">
                    <input
                      type="text"
                      value={form.operator}
                      onChange={e => setForm({ ...form, operator: e.target.value })}
                      placeholder="Nama klien"
                      list="master-operator"
                    />
                    <datalist id="master-operator">
                      {filterOptions?.operators?.map((v, i) => (
                        <option key={i} value={v} />
                      ))}
                    </datalist>
                    <button type="button" className="btn-master" title="Pilih dari data master" onClick={() => openMasterData('operator', 'Nama Klien')}>
                      <List size={16} />
                    </button>
                  </div>
                </div>

                {/* Ketua Koperasi */}
                <div className="form-group">
                  <label>Ketua Koperasi</label>
                  <input
                    type="text"
                    value={form.ketua_koperasi || ''}
                    onChange={e => setForm({ ...form, ketua_koperasi: e.target.value })}
                    placeholder="Nama ketua koperasi (muncul di cetak CM)"
                  />
                </div>

                {/* No Body */}
                <div className="form-group">
                  <label>No. Body</label>
                  <input
                    type="text"
                    value={form.body_no}
                    onChange={e => setForm({ ...form, body_no: e.target.value })}
                    placeholder="No body kendaraan"
                  />
                </div>

                {/* No Polisi */}
                <div className="form-group">
                  <label>No. Polisi *</label>
                  <input
                    type="text"
                    value={form.plate_no}
                    onChange={e => setForm({ ...form, plate_no: e.target.value })}
                    placeholder="Contoh: B 1234 ABC"
                  />
                </div>

                {/* Rute */}
                <div className="form-group">
                  <label>Rute</label>
                  <div className="input-with-btn">
                    <input
                      type="text"
                      value={form.route_code}
                      onChange={e => setForm({ ...form, route_code: e.target.value })}
                      placeholder="Kode rute"
                      list="master-route"
                    />
                    <datalist id="master-route">
                      {filterOptions?.routes?.map((v, i) => (
                        <option key={i} value={v} />
                      ))}
                    </datalist>
                    <button type="button" className="btn-master" title="Pilih dari data master" onClick={() => openMasterData('route_code', 'Rute')}>
                      <List size={16} />
                    </button>
                  </div>
                </div>

                {/* Jenis Armada */}
                <div className="form-group">
                  <label>Jenis Armada</label>
                  <div className="input-with-btn">
                    <input
                      type="text"
                      value={form.vehicle_type}
                      onChange={e => setForm({ ...form, vehicle_type: e.target.value })}
                      placeholder="Jenis armada"
                      list="master-vehicle-type"
                    />
                    <datalist id="master-vehicle-type">
                      {filterOptions?.vehicle_types?.map((v, i) => (
                        <option key={i} value={v} />
                      ))}
                    </datalist>
                    <button type="button" className="btn-master" title="Pilih dari data master" onClick={() => openMasterData('vehicle_type', 'Jenis Armada')}>
                      <List size={16} />
                    </button>
                  </div>
                </div>

                {/* AC/Non AC */}
                <div className="form-group">
                  <label>AC/Non AC</label>
                  <select value={form.jenis_ac} onChange={e => setForm({ ...form, jenis_ac: e.target.value })}>
                    <option value="Non AC">🔧 Non AC</option>
                    <option value="AC">❄ AC</option>
                  </select>
                </div>

                {/* Status */}
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.value === 'true' })}>
                    <option value="true">Aktif</option>
                    <option value="false">Non-Aktif</option>
                  </select>
                </div>

                {/* Pengendapan */}
                <div className="form-group">
                  <label>Pengendapan</label>
                  <div className="input-with-btn">
                    <input
                      type="text"
                      value={form.pengendapan}
                      onChange={e => setForm({ ...form, pengendapan: e.target.value })}
                      placeholder="Lokasi pengendapan (pool/garasi)"
                      list="master-pengendapan"
                    />
                    <datalist id="master-pengendapan">
                      {filterOptions?.pengendapan_list?.map((v, i) => (
                        <option key={i} value={v} />
                      ))}
                    </datalist>
                    <button type="button" className="btn-master" title="Pilih dari data master" onClick={() => openMasterData('pengendapan', 'Pengendapan')}>
                      <List size={16} />
                    </button>
                  </div>
                </div>

                {/* No Kontrak */}
                <div className="form-group">
                  <label>No. Kontrak</label>
                  <input
                    type="text"
                    value={form.contract_no}
                    onChange={e => setForm({ ...form, contract_no: e.target.value })}
                    placeholder="Nomor kontrak"
                  />
                </div>

                {/* Teknisi */}
                <div className="form-group">
                  <label>Teknisi</label>
                  <select
                    value={form.teknisi}
                    onChange={e => setForm({ ...form, teknisi: e.target.value })}
                  >
                    <option value="">-- Pilih Teknisi --</option>
                    {teknisiList.map((t, i) => (
                      <option key={i} value={t.nama}>{t.nama}</option>
                    ))}
                  </select>
                  {loadingTeknisi && <span className="form-hint">Memuat data teknisi...</span>}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Master Data Modal */}
      {showMasterModal && (
        <div className="modal-overlay" onClick={closeMasterModal}>
          <div className="modal-content master-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <List size={18} style={{ marginRight: 8 }} />
                Data Master — {masterData.title}
              </h3>
              <button className="btn-icon" onClick={closeMasterModal}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              {/* Add new row */}
              {masterData.field === 'operator' ? (
                // Layout 2 baris untuk Nama Klien: baris 1 = nama klien + tombol Tambah, baris 2 = ketua koperasi
                <div className="master-add-row master-add-row-2col" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="text"
                      value={masterNewValue}
                      onChange={e => setMasterNewValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addMasterData(); }}
                      placeholder="Nama klien baru..."
                      style={{ flex: 1, minWidth: 0 }}
                    />
                    <button type="button" className="btn btn-primary btn-sm" onClick={addMasterData} disabled={masterSaving}>
                      <Plus size={14} />
                      {masterSaving ? 'Menyimpan...' : 'Tambah'}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={masterKetuaValue}
                    onChange={e => setMasterKetuaValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addMasterData(); }}
                    placeholder="Ketua Koperasi (opsional)"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              ) : (
                <div className="master-add-row">
                  <input
                    type="text"
                    value={masterNewValue}
                    onChange={e => setMasterNewValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addMasterData(); }}
                    placeholder={`Tambah ${masterData.title} baru...`}
                  />
                  <button type="button" className="btn btn-primary btn-sm" onClick={addMasterData} disabled={masterSaving}>
                    <Plus size={14} />
                    {masterSaving ? 'Menyimpan...' : 'Tambah'}
                  </button>
                </div>
              )}
              {masterAddHint && (
                <div style={{ fontSize: '0.78rem', color: 'var(--danger)', margin: '-6px 0 8px 2px' }}>
                  {masterAddHint}
                </div>
              )}

              {/* Search */}
              <div className="master-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Cari..."
                  onChange={e => {
                    const q = e.target.value.toLowerCase();
                    const filtered = masterData.options.filter(o => o.toLowerCase().includes(q));
                    setMasterData(prev => ({ ...prev, options: filtered }));
                    if (!q) {
                      setMasterData(prev => ({ ...prev, options: getOptionsFromData(filterOptions, prev.field) }));
                    }
                  }}
                />
              </div>

              {/* List */}
              <div className="master-list">
                {masterData.options.length === 0 ? (
                  <p className="text-muted text-center" style={{ padding: '24px 0' }}>
                    Belum ada data. Gunakan form di atas untuk menambah data baru.
                  </p>
                ) : (
                  masterData.options.map((item, i) => (
                    <div key={i} className="master-item-row">
                      {editingMasterItem === item ? (
                        <div className="master-item-edit">
                          <input
                            type="text"
                            value={masterEditValue}
                            onChange={e => setMasterEditValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveEditMasterItem(); if (e.key === 'Escape') setEditingMasterItem(null); }}
                            autoFocus
                          />
                          <button className="btn btn-primary btn-sm" onClick={saveEditMasterItem} disabled={masterSaving}>
                            <CheckCircle size={14} />
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditingMasterItem(null)}>
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            className="master-item"
                            onClick={() => selectMasterData(item)}
                          >
                            <span>{item}</span>
                            <CheckCircle size={14} className="master-check" />
                          </button>
                          <div className="master-item-actions">
                            <button className="btn-icon btn-xs" title="Edit" onClick={() => startEditMasterItem(item)}>
                              <Edit2 size={13} />
                            </button>
                            <button className="btn-icon btn-xs btn-danger" title="Hapus" onClick={() => deleteMasterDataItem(item)}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </>
                      )}
                      {masterData.field === 'operator' && (
                        <div className="master-item-ketua" style={{ width: '100%', padding: '0 0 8px 14px', fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                          {editKetuaFor === item ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="text"
                                value={editKetuaValue}
                                onChange={e => setEditKetuaValue(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveKetua(item); if (e.key === 'Escape') setEditKetuaFor(null); }}
                                placeholder="Ketua Koperasi"
                                autoFocus
                                style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-300)', fontSize: '0.8rem', boxSizing: 'border-box' }}
                              />
                              <button className="btn-icon btn-xs" title="Simpan" onClick={() => saveKetua(item)} disabled={masterSaving}>
                                <CheckCircle size={13} style={{ color: '#2dce89' }} />
                              </button>
                              <button className="btn-icon btn-xs" title="Batal" onClick={() => setEditKetuaFor(null)}>
                                <X size={13} />
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span className="master-ketua-label">Ketua Koperasi:</span>
                              <strong style={{ color: 'var(--gray-700)', fontWeight: 600 }}>{klienList.find(k => k.nama === item)?.ketua_koperasi || '-'}</strong>
                              <button
                                className="btn-icon btn-xs"
                                title="Ubah ketua koperasi"
                                onClick={() => { setEditKetuaFor(item); setEditKetuaValue(klienList.find(k => k.nama === item)?.ketua_koperasi || ''); }}
                              >
                                <Edit2 size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeMasterModal}>Tutup</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
